import React, { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, CONTACT_EMAIL } from "@/lib/config";
import { Mail, ArrowRight, Loader2 } from "lucide-react";

function Field({ label, required, labelCls, children }) {
  return (
    <div>
      <Label className={labelCls}>
        {label}
        {required && <span className="text-burgundy"> *</span>}
      </Label>
      {children}
    </div>
  );
}

export default function EnquiryForm({ onDark = false, onSuccess }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", guests: "", purpose: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please include your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        arrival: null,
        departure: null,
        guests: form.guests ? Number(form.guests) : null,
        purpose: form.purpose || null,
        message: form.message,
      });
      toast.success("Your letter is on its way.");
      setForm({ name: "", email: "", phone: "", guests: "", purpose: "", message: "" });
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error("Something went awry. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = `eyebrow mb-2 block ${onDark ? "text-parchment/70" : ""}`;
  const inputCls = `ledger-input ${onDark ? "ledger-input-dark" : ""}`;

  return (
    <form className="space-y-6" onSubmit={onSubmit} data-testid="enquiry-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Your Name" required labelCls={labelCls}>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="enquiry-name" />
        </Field>
        <Field label="Email" required labelCls={labelCls}>
          <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="enquiry-email" />
        </Field>
        <Field label="Telephone" labelCls={labelCls}>
          <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="enquiry-phone" />
        </Field>
        <Field label="Guests (up to 40)" labelCls={labelCls}>
          <Input type="number" min="1" max="40" value={form.guests} onChange={(e) => set("guests", e.target.value)} className={inputCls} data-testid="enquiry-guests" />
        </Field>
      </div>

      <Field label="Purpose of the visit" labelCls={labelCls}>
        <Input value={form.purpose} onChange={(e) => set("purpose", e.target.value)} placeholder="A residency, a birthday, a garden hire…" className={inputCls} data-testid="enquiry-purpose" />
      </Field>

      <Field label="Your letter" required labelCls={labelCls}>
        <Textarea rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us who is coming, what you'd like to do, any dates you have in mind…" className={`${inputCls} !h-auto`} data-testid="enquiry-message" />
      </Field>

      <div className="flex flex-col sm:flex-row sm:items-center gap-5 pt-1">
        <button
          type="submit"
          disabled={submitting}
          className="bg-burgundy text-parchment px-8 py-3.5 font-sans text-[0.74rem] uppercase tracking-[0.26em] hover:bg-ink transition-colors inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="enquiry-submit"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : <>Send letter <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></>}
        </button>
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className={`font-sans text-[0.7rem] uppercase tracking-[0.22em] inline-flex items-center gap-2 ${onDark ? "text-parchment/70 hover:text-parchment" : "text-ink/70 hover:text-burgundy"}`}
          data-testid="enquiry-email-direct"
        >
          <Mail className="w-4 h-4" strokeWidth={1.5} /> Or write direct: {CONTACT_EMAIL}
        </a>
      </div>
    </form>
  );
}
